import React, { useEffect, useState, useRef } from "react";
import { X, FileText } from "lucide-react";
import classes from "./CreateProductionModal.module.css";
import axios from "axios";
import { BASE_URL } from "../../Components/API/Api";
import Swal from "sweetalert2";

const CreateProductionModal = ({ isOpen, onClose, refreshOrders, companyId: propCompanyId }) => {
    const [productionNo, setProductionNo] = useState("");
    const [products, setProducts] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [companyId, setCompanyId] = useState(propCompanyId || null);

    const [formData, setFormData] = useState({
        target_product_id: "",
        planned_quantity: "",
        ware_house_id: "",
        start_date: "",
        expected_end_date: "",
    });

    const [loading, setLoading] = useState(false);
    const [warehouseLoading, setWarehouseLoading] = useState(false);
    const [productLoading, setProductLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // For searchable select
    const [searchTerm, setSearchTerm] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    // Get company ID from props or localStorage
    useEffect(() => {
        // If companyId is provided as prop, use it
        if (propCompanyId) {
            setCompanyId(propCompanyId);
            return;
        }

        // Otherwise try to get from localStorage
        const getCompanyId = () => {
            console.log("Attempting to get company ID from various sources...");
            
            const directCompanyId = localStorage.getItem("company_id");
            if (directCompanyId) {
                console.log("Found company_id in localStorage:", directCompanyId);
                setCompanyId(directCompanyId);
                return;
            }
            
            const companyInfo = localStorage.getItem("companyInfo");
            if (companyInfo) {
                try {
                    const parsed = JSON.parse(companyInfo);
                    const id = parsed?.id || parsed?.company_id || parsed?.companyId;
                    if (id) {
                        setCompanyId(String(id));
                        console.log("Using company ID from companyInfo:", id);
                        return;
                    }
                } catch (error) {
                    console.error("Error parsing companyInfo:", error);
                }
            }
            
            const userData = localStorage.getItem("user");
            if (userData) {
                try {
                    const parsed = JSON.parse(userData);
                    
                    if (parsed.company_id) {
                        setCompanyId(parsed.company_id);
                        console.log("Using direct company_id from user:", parsed.company_id);
                        return;
                    }
                    
                    if (parsed.company) {
                        const companyId = parsed.company?.id || parsed.company?.company_id || parsed.company?.companyId;
                        if (companyId) {
                            setCompanyId(String(companyId));
                            console.log("Using company ID from user.company:", companyId);
                            return;
                        }
                    }
                    
                    if (parsed.companies && Array.isArray(parsed.companies) && parsed.companies.length > 0) {
                        const mainCompany = parsed.companies.find(c => c.is_main === "1" || c.is_main === 1);
                        if (mainCompany) {
                            const companyId = mainCompany?.id || mainCompany?.company_id || mainCompany?.companyId;
                            if (companyId) {
                                setCompanyId(String(companyId));
                                console.log("Using main company ID from companies array:", companyId);
                                return;
                            }
                        }
                        
                        const firstCompany = parsed.companies[0];
                        if (firstCompany) {
                            const companyId = firstCompany?.id || firstCompany?.company_id || firstCompany?.companyId;
                            if (companyId) {
                                setCompanyId(String(companyId));
                                console.log("Using first company ID from companies array:", companyId);
                                return;
                            }
                        }
                    }
                } catch (error) {
                    console.error("Error parsing user data:", error);
                }
            }
            
            console.warn("No company ID found in any source");
        };
        
        getCompanyId();
    }, [propCompanyId]);

    useEffect(() => {
        if (isOpen) {
            if (companyId) {
                generateProductionNumber();
                fetchWarehouses();
                fetchProducts();
            } else {
                console.warn("Cannot fetch data: Company ID not available");
                Swal.fire({
                    icon: 'warning',
                    title: 'Company ID Missing',
                    text: 'Please select a company and try again.',
                    confirmButtonText: 'OK'
                }).then(() => {
                    onClose();
                });
            }
        }
    }, [isOpen, companyId]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const generateProductionNumber = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("authToken");
            
            if (!token) {
                console.error("No auth token found");
                Swal.fire({
                    icon: 'error',
                    title: 'Authentication Error',
                    text: 'Please log in again'
                });
                return;
            }

            if (!companyId) {
                console.error("Company ID is required");
                return;
            }

            const response = await axios.get(
                `${BASE_URL}/v1/production_order/generate_number`,
                { 
                    headers: { Authorization: `Bearer ${token}` },
                    params: { company_id: companyId }
                }
            );
            
            if (response.data?.data) {
                setProductionNo(response.data.data);
            }
        } catch (error) {
            console.error("Error generating production number:", error);
            
            if (error.response) {
                console.error("Error response:", error.response.data);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.response.data?.message || 'Failed to generate production number'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchWarehouses = async () => {
        try {
            setWarehouseLoading(true);
            const token = localStorage.getItem("authToken");
            
            if (!token) {
                console.error("No auth token found");
                return;
            }

            if (!companyId) {
                console.error("Company ID is required");
                return;
            }

            const response = await axios.get(`${BASE_URL}/v1/warehouse`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { company_id: companyId }
            });
            
            if (Array.isArray(response.data?.data)) {
                setWarehouses(response.data.data);
            } else {
                console.warn("Unexpected warehouse response format:", response.data);
                setWarehouses([]);
            }
        } catch (error) {
            console.error("Error fetching warehouses:", error);
            
            if (error.response) {
                console.error("Error response:", error.response.data);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.response.data?.message || 'Failed to fetch warehouses'
                });
            }
            
            setWarehouses([]);
        } finally {
            setWarehouseLoading(false);
        }
    };

    const fetchProducts = async () => {
        try {
            setProductLoading(true);
            const token = localStorage.getItem("authToken");
            
            if (!token) {
                console.error("No auth token found");
                return;
            }

            if (!companyId) {
                console.error("Company ID is required");
                return;
            }

            const response = await axios.get(`${BASE_URL}/v1/product/finished_goods`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { company_id: companyId }
            });
            
            if (Array.isArray(response.data?.data)) {
                setProducts(response.data.data);
            } else {
                console.warn("Unexpected products response format:", response.data);
                setProducts([]);
            }
        } catch (error) {
            console.error("Error fetching products:", error);
            
            if (error.response) {
                console.error("Error response:", error.response.data);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.response.data?.message || 'Failed to fetch products'
                });
            }
            
            setProducts([]);
        } finally {
            setProductLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleProductSelect = (productId) => {
        setFormData(prev => ({
            ...prev,
            target_product_id: productId
        }));
        setShowDropdown(false);
        setSearchTerm("");
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        if (!showDropdown) {
            setShowDropdown(true);
        }
    };

    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.code && product.code.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const getSelectedProductName = () => {
        if (!formData.target_product_id) return "";
        const product = products.find(p => p.id === formData.target_product_id);
        return product ? product.name : "";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!companyId) {
            Swal.fire({
                icon: 'error',
                title: 'Company ID Error',
                text: 'Company ID not found. Please try again.'
            });
            return;
        }

        if (
            !formData.target_product_id ||
            !formData.planned_quantity ||
            !formData.start_date ||
            !formData.expected_end_date
        ) {
            Swal.fire("Error", "Please fill in all required fields", "error");
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem("authToken");

            const payload = {
                production_number: productionNo,
                target_product_id: formData.target_product_id,
                planned_quantity: formData.planned_quantity,
                start_date: formData.start_date,
                expected_end_date: formData.expected_end_date,
                ware_house_id: formData.ware_house_id || null,
                company_id: companyId
            };

            console.log("Creating production order with payload:", payload);

            const response = await axios.post(
                `${BASE_URL}/v1/production_order/create`,
                payload,
                { 
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    } 
                }
            );

            console.log("Create response:", response.data);

            if (response.data?.status) {
                Swal.fire({
                    title: "Success",
                    text: response.data.message || "Production order created successfully",
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false
                }).then(() => {
                    setFormData({
                        target_product_id: "",
                        planned_quantity: "",
                        ware_house_id: "",
                        start_date: "",
                        expected_end_date: "",
                    });
                    setSearchTerm("");
                    setProductionNo("");
                    onClose();
                    if (refreshOrders) refreshOrders();
                });
            } else {
                let errorMessage = "Failed to create production order";
                if (response.data?.message) {
                    if (typeof response.data.message === 'object') {
                        errorMessage = Object.values(response.data.message).flat().join(', ');
                    } else {
                        errorMessage = response.data.message;
                    }
                }
                
                Swal.fire("Error", errorMessage, "error");
            }
        } catch (error) {
            console.error("Error creating production order:", error);
            
            let errorMessage = "Something went wrong while creating the order";
            if (error.response?.data?.message) {
                if (typeof error.response.data.message === 'object') {
                    errorMessage = Object.values(error.response.data.message).flat().join(', ');
                } else {
                    errorMessage = error.response.data.message;
                }
            }
            
            Swal.fire("Error", errorMessage, "error");
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className={classes.overlay}>
            <div className={classes.modal}>
                <div className={classes.header}>
                    <div className={classes.title}>
                        <FileText className={classes.icon} />
                        <h2>Create Production Order</h2>
                    </div>
                    <button className={classes.closeBtn} onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className={classes.formGrid}>
                        <div className={classes.formGroup}>
                            <label>Production No</label>
                            <input
                                type="text"
                                value={loading ? "Generating..." : productionNo}
                                readOnly
                            />
                        </div>

                        <div className={classes.formGroup}>
                            <label>Product to Manufacture</label>
                            <div className={classes.searchSelectWrapper} ref={dropdownRef}>
                                <div className={classes.searchInputContainer}>
                                    <input
                                        type="text"
                                        className={classes.searchSelectInput}
                                        placeholder="Search and select product..."
                                        value={showDropdown ? searchTerm : getSelectedProductName()}
                                        onChange={handleSearchChange}
                                        onClick={() => setShowDropdown(true)}
                                        onFocus={() => setShowDropdown(true)}
                                    />
                                </div>

                                {showDropdown && (
                                    <div className={classes.searchDropdown}>
                                        <div className={classes.dropdownList}>
                                            {productLoading ? (
                                                <div className={classes.dropdownItem}>Loading products...</div>
                                            ) : filteredProducts.length === 0 ? (
                                                <div className={classes.dropdownItem}>No products found</div>
                                            ) : (
                                                filteredProducts.map((product) => (
                                                    <div
                                                        key={product.id}
                                                        className={`${classes.dropdownItem} ${formData.target_product_id === product.id ? classes.selectedItem : ''}`}
                                                        onClick={() => handleProductSelect(product.id)}
                                                    >
                                                        {product.name}
                                                        {product.code && (
                                                            <span className={classes.productCode}> ({product.code})</span>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={classes.formGroup}>
                            <label>Planned Quantity</label>
                            <input
                                type="number"
                                name="planned_quantity"
                                placeholder="10"
                                value={formData.planned_quantity}
                                onChange={handleChange}
                            />
                        </div>

                        <div className={classes.formGroup}>
                            <label>
                                Warehouse <span style={{ color: "#999" }}>(optional)</span>
                            </label>
                            <select
                                name="ware_house_id"
                                value={formData.ware_house_id}
                                onChange={handleChange}
                            >
                                <option value="">
                                    {warehouseLoading ? "Loading warehouses..." : "-- Select Warehouse --"}
                                </option>
                                {!warehouseLoading && warehouses.map((wh) => (
                                    <option key={wh.id} value={wh.id}>
                                        {wh.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={classes.formGroup}>
                            <label>Start Date</label>
                            <input
                                type="date"
                                name="start_date"
                                value={formData.start_date}
                                onChange={handleChange}
                            />
                        </div>

                        <div className={classes.formGroup}>
                            <label>Expected End Date</label>
                            <input
                                type="date"
                                name="expected_end_date"
                                value={formData.expected_end_date}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <button type="submit" className={classes.saveBtn} disabled={submitting}>
                        {submitting ? "Creating..." : "Create Production Order"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CreateProductionModal;